// Iter 125 — Permissions admin UI (PM-only).
// Tab 1 (Roles): list system + custom roles, create / edit / delete custom roles
// Tab 2 (Users): pick a user → see effective perms + grant/deny per-user override

import { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { Loader2, ShieldCheck, Users as UsersIcon, Plus, Trash2, Pencil, Lock, ArrowLeft } from 'lucide-react';
import Header from '../components/Header';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const HUB_LABEL = {
  dashboard: 'Dashboard', clinical: 'Clinical', knowledge: 'Knowledge',
  operations: 'Operations', compliance: 'Compliance',
};
const DEST_LABEL = {
  consent: 'Consent forms', anticoag: 'Anticoagulation', preparation: 'Patient Prep',
  sops: 'SOPs', training: 'Kestral K4', mbs: 'MBS Monitor', referrals: 'Referral Base', 'quick-ref': 'Quick Reference',
  roster: 'Rostering', equipment: 'Equipment register', people: 'People',
  letterhead: 'Letterhead Generator', golive: 'Go-Live Tracker', management: 'Practice settings',
  permissions: 'Permissions',
  compliance: 'Compliance Hub',
};

export default function PermissionsPage() {
  const [tab, setTab] = useState('roles');
  return (
    <div className="min-h-screen bg-background">
      <Header />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        <div className="mb-6">
          <div className="text-xs font-bold tracking-[0.18em] uppercase mb-1" style={{ color: '#C2410C' }}>
            OPERATIONS · ACCESS CONTROL
          </div>
          <h1 className="text-3xl font-bold tracking-tight" style={{ color: '#0d2644' }}>Permissions</h1>
          <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
            Decide who can see which sections of i3 — by role (defaults) or individual user (overrides).
          </p>
        </div>

        <div className="inline-flex items-center gap-1 rounded-lg border bg-card p-1 mb-5">
          <button onClick={() => setTab('roles')} data-testid="perms-tab-roles"
            className={`text-sm font-semibold px-4 py-1.5 rounded-md inline-flex items-center gap-1.5 ${tab==='roles' ? 'bg-[#0d2644] text-white' : 'text-muted-foreground'}`}>
            <ShieldCheck className="w-4 h-4" /> Roles
          </button>
          <button onClick={() => setTab('users')} data-testid="perms-tab-users"
            className={`text-sm font-semibold px-4 py-1.5 rounded-md inline-flex items-center gap-1.5 ${tab==='users' ? 'bg-[#0d2644] text-white' : 'text-muted-foreground'}`}>
            <UsersIcon className="w-4 h-4" /> Users
          </button>
        </div>

        {tab === 'roles' && <RolesTab />}
        {tab === 'users' && <UsersTab />}
      </div>
    </div>
  );
}

/* ──────────────────────── ROLES TAB ──────────────────────── */
function RolesTab() {
  const [roles, setRoles] = useState(null);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    const r = await axios.get(`${API}/permissions/roles`, { withCredentials: true });
    setRoles(r.data.roles);
  };
  useEffect(() => { load(); }, []);

  const remove = async (role) => {
    if (!window.confirm(`Delete role "${role.name}"? Users currently assigned to it will lose its grants.`)) return;
    await axios.delete(`${API}/permissions/roles/${role.role_id}`, { withCredentials: true });
    load();
  };

  if (!roles) return <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />;
  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-sm font-bold uppercase tracking-wider text-muted-foreground">All roles</h2>
        <button onClick={() => setEditing({ role_id: null, name: '', description: '', granted_hubs: [], granted_destinations: [] })}
          data-testid="role-new"
          className="text-sm font-semibold inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-lg bg-[#0d2644] text-white">
          <Plus className="w-4 h-4" /> New custom role
        </button>
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        {roles.map(r => (
          <div key={r.role_id} className="rounded-lg border bg-card p-4" data-testid={`role-card-${r.role_id}`}>
            <div className="flex items-start justify-between gap-2 mb-1">
              <div>
                <h3 className="font-bold text-base" style={{ color: '#0d2644' }}>{r.name}</h3>
                <span className="text-[10px] font-bold uppercase tracking-wider"
                  style={{ color: r.kind === 'system' ? '#475569' : '#7c3aed' }}>
                  {r.kind === 'system' ? <>System <Lock className="w-2.5 h-2.5 inline -mt-0.5" /></> : 'Custom'}
                </span>
              </div>
              {r.kind !== 'system' && (
                <div className="flex gap-1">
                  <button onClick={() => setEditing({ ...r })} className="p-1.5 rounded hover:bg-muted" data-testid={`role-edit-${r.role_id}`}>
                    <Pencil className="w-3.5 h-3.5" />
                  </button>
                  <button onClick={() => remove(r)} className="p-1.5 rounded hover:bg-red-50 text-red-600" data-testid={`role-delete-${r.role_id}`}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              )}
            </div>
            {r.description && <p className="text-xs text-muted-foreground mb-2">{r.description}</p>}
            <div className="text-xs">
              <div className="font-semibold text-muted-foreground">Hubs ({r.granted_hubs.length}):</div>
              <div className="text-foreground">{r.granted_hubs.map(h => HUB_LABEL[h]||h).join(' · ') || '—'}</div>
            </div>
            <div className="text-xs mt-2">
              <div className="font-semibold text-muted-foreground">Destinations ({r.granted_destinations.length}):</div>
              <div className="text-foreground">{r.granted_destinations.map(d => DEST_LABEL[d]||d).join(' · ') || '—'}</div>
            </div>
          </div>
        ))}
      </div>
      {editing && <RoleEditor role={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}

function RoleEditor({ role, onClose, onSaved }) {
  const [name, setName] = useState(role.name || '');
  const [desc, setDesc] = useState(role.description || '');
  const [hubs, setHubs] = useState(new Set(role.granted_hubs || []));
  const [dests, setDests] = useState(new Set(role.granted_destinations || []));
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const save = async () => {
    setBusy(true); setErr('');
    const body = {
      name: name.trim(), description: desc.trim() || null,
      granted_hubs: Array.from(hubs), granted_destinations: Array.from(dests),
    };
    try {
      if (role.role_id) {
        await axios.patch(`${API}/permissions/roles/${role.role_id}`, body, { withCredentials: true });
      } else {
        await axios.post(`${API}/permissions/roles`, body, { withCredentials: true });
      }
      onSaved();
    } catch (e) { setErr(e?.response?.data?.detail || 'Save failed'); setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl max-w-2xl w-full p-5 shadow-xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}
           data-testid="role-editor-modal">
        <h3 className="text-lg font-bold mb-3">{role.role_id ? 'Edit role' : 'New custom role'}</h3>
        <div className="space-y-3">
          <label className="block text-xs font-semibold text-muted-foreground">Name
            <Input value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Radiographer" className="mt-1" data-testid="role-name" />
          </label>
          <label className="block text-xs font-semibold text-muted-foreground">Description
            <Textarea value={desc} onChange={e => setDesc(e.target.value)} rows={2} className="mt-1" placeholder="Short blurb about what this role can do" />
          </label>
          <PermissionGrid hubs={hubs} setHubs={setHubs} dests={dests} setDests={setDests} mode="grant" />
          {err && <div className="text-xs text-red-600">{err}</div>}
        </div>
        <div className="flex justify-end gap-2 mt-5">
          <button onClick={onClose} className="text-sm font-semibold px-4 py-2 rounded-lg border">Cancel</button>
          <button onClick={save} disabled={!name.trim() || busy} className="text-sm font-semibold px-4 py-2 rounded-lg bg-[#0d2644] text-white disabled:opacity-50"
                  data-testid="role-save">
            {busy ? 'Saving…' : 'Save role'}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ──────────────────────── USERS TAB ──────────────────────── */
function UsersTab() {
  const [users, setUsers] = useState(null);
  const [selected, setSelected] = useState(null);
  const [resetResult, setResetResult] = useState(null);

  const load = async () => {
    const r = await axios.get(`${API}/permissions/users`, { withCredentials: true });
    setUsers(r.data.users);
  };
  useEffect(() => { load(); }, []);

  const onResetPassword = async (u) => {
    const ok = window.confirm(
      `Reset password for ${u.name || u.email}?\n\n` +
      `A new temporary password will be generated, emailed to them, and they'll be forced to choose a new one on next login.`
    );
    if (!ok) return;
    try {
      const r = await axios.post(`${API}/auth/admin-reset-password`,
        { user_id: u.id, notify: true },
        { withCredentials: true });
      setResetResult({ user: u, ...r.data });
    } catch (e) {
      alert(e?.response?.data?.detail || 'Could not reset password');
    }
  };

  if (!users) return <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />;
  if (selected) return <UserEditor userId={selected} onBack={() => { setSelected(null); load(); }} />;

  return (
    <div>
      <div className="rounded-lg border bg-card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-muted/40 text-xs uppercase tracking-wider">
            <tr>
              <th className="text-left px-3 py-2">Name</th>
              <th className="text-left px-3 py-2">Email</th>
              <th className="text-left px-3 py-2">Base role</th>
              <th className="text-left px-3 py-2">Custom roles</th>
              <th className="text-left px-3 py-2">Access</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {users.map(u => (
              <tr key={u.id} className="border-t" data-testid={`perms-user-${u.id}`}>
                <td className="px-3 py-2 font-semibold" style={{ color: '#0d2644' }}>{u.name || '—'}</td>
                <td className="px-3 py-2 text-muted-foreground">{u.email}</td>
                <td className="px-3 py-2">
                  <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded bg-muted">{u.role}</span>
                </td>
                <td className="px-3 py-2 text-xs">{u.assigned_role_ids.length || '—'}</td>
                <td className="px-3 py-2 text-xs"><strong>{u.hub_count}</strong> hubs · <strong>{u.destination_count}</strong> dests</td>
                <td className="px-3 py-2 text-right">
                  <div className="inline-flex items-center gap-1.5">
                    <button onClick={() => onResetPassword(u)} data-testid={`perms-user-reset-pw-${u.id}`}
                      title="Generate a new temp password + email it to the user"
                      className="text-xs font-semibold px-2.5 py-1 rounded border hover:bg-amber-50 hover:border-amber-300 hover:text-amber-700">
                      Reset pw
                    </button>
                    <button onClick={() => setSelected(u.id)} data-testid={`perms-user-edit-${u.id}`}
                      className="text-xs font-semibold px-3 py-1 rounded border">Manage</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {resetResult && (
        <ResetPasswordResultModal data={resetResult} onClose={() => setResetResult(null)} />
      )}
    </div>
  );
}

function ResetPasswordResultModal({ data, onClose }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard?.writeText(data.temp_password);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={e => e.stopPropagation()} data-testid="reset-pw-result-modal"
           className="bg-card rounded-xl border max-w-md w-full shadow-2xl p-5">
        <h2 className="text-lg font-bold mb-1" style={{ color: '#0d2644' }}>
          Password reset for {data.user.name || data.user.email}
        </h2>
        <div className={`text-xs mb-3 ${data.emailed ? 'text-green-700' : 'text-amber-700'}`} data-testid="reset-pw-status">
          {data.emailed
            ? `✓ Temp password emailed to ${data.user_email}.`
            : `⚠ Email did not send (${data.email_error || 'unknown'}). Share the temp password with the user manually.`}
        </div>
        <div className="bg-[#0f172a] rounded-lg p-3 font-mono text-[#52c3c5] text-lg tracking-wider text-center font-bold mb-2"
             data-testid="reset-pw-temp">
          {data.temp_password}
        </div>
        <div className="flex items-center justify-between gap-2">
          <button onClick={copy} data-testid="reset-pw-copy"
                  className="text-xs font-semibold px-3 py-1.5 rounded border hover:bg-muted/40">
            {copied ? '✓ Copied' : 'Copy temp password'}
          </button>
          <button onClick={onClose} data-testid="reset-pw-close"
                  className="text-xs font-semibold px-3 py-1.5 rounded bg-[#0d2644] text-white">
            Done
          </button>
        </div>
        <p className="text-[11px] text-muted-foreground mt-3 leading-relaxed">
          The user is now forced to choose a new password the next time they log in.
          This temp password is shown <strong>once</strong> — close this dialog and it&apos;s gone.
        </p>
      </div>
    </div>
  );
}

function UserEditor({ userId, onBack }) {
  const [data, setData] = useState(null);
  const [roles, setRoles] = useState([]);
  const [grantedHubs, setGrantedHubs] = useState(new Set());
  const [grantedDests, setGrantedDests] = useState(new Set());
  const [deniedHubs, setDeniedHubs] = useState(new Set());
  const [deniedDests, setDeniedDests] = useState(new Set());
  const [assignedRoleIds, setAssignedRoleIds] = useState(new Set());
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const load = async () => {
    const [u, r] = await Promise.all([
      axios.get(`${API}/permissions/users/${userId}`, { withCredentials: true }),
      axios.get(`${API}/permissions/roles`, { withCredentials: true }),
    ]);
    setData(u.data); setRoles(r.data.roles);
    const o = u.data.permission_overrides || {};
    setGrantedHubs(new Set(o.granted_hubs || []));
    setGrantedDests(new Set(o.granted_destinations || []));
    setDeniedHubs(new Set(o.denied_hubs || []));
    setDeniedDests(new Set(o.denied_destinations || []));
    setAssignedRoleIds(new Set(u.data.assigned_role_ids || []));
  };
  useEffect(() => { load(); }, [userId]);

  const save = async () => {
    setBusy(true); setErr('');
    try {
      await axios.patch(`${API}/permissions/users/${userId}`, {
        assigned_role_ids: Array.from(assignedRoleIds),
        granted_hubs: Array.from(grantedHubs),
        granted_destinations: Array.from(grantedDests),
        denied_hubs: Array.from(deniedHubs),
        denied_destinations: Array.from(deniedDests),
      }, { withCredentials: true });
      // Reload to reflect new effective perms
      await load();
      setBusy(false);
    } catch (e) { setErr(e?.response?.data?.detail || 'Save failed'); setBusy(false); }
  };

  const toggle = (set, setter, id) => {
    const next = new Set(set);
    next.has(id) ? next.delete(id) : next.add(id);
    setter(next);
  };

  if (!data) return <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />;
  const customRoles = roles.filter(r => r.kind !== 'system');

  return (
    <div>
      <button onClick={onBack} className="text-xs font-semibold inline-flex items-center gap-1 text-muted-foreground hover:text-foreground mb-3" data-testid="perms-user-back">
        <ArrowLeft className="w-3 h-3" /> Back to user list
      </button>

      <div className="rounded-lg border bg-card p-4 mb-4">
        <h2 className="text-lg font-bold" style={{ color: '#0d2644' }}>{data.name}</h2>
        <p className="text-sm text-muted-foreground">{data.email}</p>
        <div className="mt-2 flex items-center gap-2 text-xs flex-wrap">
          <span className="font-bold uppercase tracking-wider text-muted-foreground">Base role:</span>
          <span className="px-2 py-0.5 rounded bg-muted font-semibold">{data.role}</span>
          <span className="text-muted-foreground">·</span>
          <span><strong>{data.effective?.hubs.length || 0}</strong> hubs · <strong>{data.effective?.destinations.length || 0}</strong> destinations granted</span>
        </div>
      </div>

      {customRoles.length > 0 && (
        <div className="rounded-lg border bg-card p-4 mb-4">
          <h3 className="text-sm font-bold uppercase tracking-wider text-muted-foreground mb-2">Custom roles assigned</h3>
          <div className="flex gap-2 flex-wrap">
            {customRoles.map(r => {
              const on = assignedRoleIds.has(r.role_id);
              return (
                <button key={r.role_id} onClick={() => toggle(assignedRoleIds, setAssignedRoleIds, r.role_id)}
                        data-testid={`perms-role-toggle-${r.role_id}`}
                        className={`text-xs font-semibold px-3 py-1.5 rounded-lg border ${on ? 'bg-[#7c3aed] text-white border-[#7c3aed]' : 'bg-card'}`}>
                  {on ? '✓ ' : ''}{r.name}
                </button>
              );
            })}
          </div>
        </div>
      )}

      <div className="rounded-lg border bg-card p-4 mb-4">
        <h3 className="text-sm font-bold uppercase tracking-wider text-muted-foreground mb-2">Per-user overrides (in addition to role)</h3>
        <PermissionGrid
          hubs={grantedHubs} setHubs={setGrantedHubs}
          dests={grantedDests} setDests={setGrantedDests}
          mode="grant"
        />
        <div className="border-t mt-4 pt-4">
          <h4 className="text-xs font-bold uppercase tracking-wider text-red-700 mb-2">Explicitly DENY (overrides any grants)</h4>
          <PermissionGrid
            hubs={deniedHubs} setHubs={setDeniedHubs}
            dests={deniedDests} setDests={setDeniedDests}
            mode="deny"
          />
        </div>
      </div>

      {err && <div className="text-xs text-red-600 mb-2">{err}</div>}
      <div className="flex items-center gap-2">
        <button onClick={save} disabled={busy} data-testid="perms-user-save"
          className="text-sm font-semibold px-4 py-2 rounded-lg bg-[#0d2644] text-white disabled:opacity-50">
          {busy ? 'Saving…' : 'Save changes'}
        </button>
        <span className="text-xs text-muted-foreground">Effective grants update immediately after save.</span>
      </div>
    </div>
  );
}

/* ──────────────────────── Shared checklist grid ──────────────────────── */
const ALL_HUBS = ['dashboard', 'clinical', 'knowledge', 'operations', 'compliance'];
const ALL_DESTS_BY_HUB = {
  clinical: ['consent', 'anticoag', 'preparation'],
  knowledge: ['sops', 'training', 'mbs', 'referrals', 'quick-ref'],
  operations: ['roster', 'equipment', 'people', 'letterhead', 'golive', 'management', 'permissions'],
  compliance: ['compliance'],
  dashboard: [],
};

function PermissionGrid({ hubs, setHubs, dests, setDests, mode }) {
  const toggleHub = (id) => { const n = new Set(hubs); n.has(id)?n.delete(id):n.add(id); setHubs(n); };
  const toggleDest = (id) => { const n = new Set(dests); n.has(id)?n.delete(id):n.add(id); setDests(n); };
  const accent = mode === 'deny' ? '#dc2626' : '#15803d';
  const accentBg = mode === 'deny' ? '#fef2f2' : '#f0fdf4';

  return (
    <div className="space-y-3">
      {ALL_HUBS.map(hub => (
        <div key={hub} className="rounded border bg-muted/30 p-2">
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={hubs.has(hub)} onChange={() => toggleHub(hub)}
                   data-testid={`perms-hub-${mode}-${hub}`}
                   className="accent-current" style={{ color: accent }} />
            <span className="font-bold" style={{ color: hubs.has(hub) ? accent : undefined }}>{HUB_LABEL[hub]}</span>
            {hubs.has(hub) && (
              <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded"
                    style={{ background: accentBg, color: accent }}>
                {mode === 'deny' ? 'denied' : 'all granted'}
              </span>
            )}
          </label>
          {ALL_DESTS_BY_HUB[hub] && ALL_DESTS_BY_HUB[hub].length > 0 && (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-3 gap-y-1 mt-1 ml-6">
              {ALL_DESTS_BY_HUB[hub].map(d => (
                <label key={d} className="inline-flex items-center gap-1.5 text-xs cursor-pointer">
                  <input type="checkbox" checked={dests.has(d)} onChange={() => toggleDest(d)}
                         data-testid={`perms-dest-${mode}-${d}`}
                         disabled={hubs.has(hub) && mode === 'grant'}
                         className="accent-current" style={{ color: accent }} />
                  <span className={hubs.has(hub) && mode === 'grant' ? 'text-muted-foreground' : ''}>{DEST_LABEL[d]}</span>
                </label>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
